;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { cloneError } from './clone-error'
import { safeStructuredClone } from './safe-structured-clone'

/** Regex patterns for sensitive field names that will be redacted when part of the log context */
const SENSITIVE_FIELD_PATTERNS = [
  /password/i,
  /secret/i,
  /token/i,
  /key/i,
  /auth/i,
  /cookie/i,
]

/**
 * Redacts sensitive fields from a payload.
 * @example
 * const payload = {
 *   password: 'do-not-expose-this',
 *   secret: 'or-this',
 *   nested: {
 *     accessToken: 'do-not-expose-this',
 *   },
 *   deeplyNested: {
 *     nested: {
 *       password: 'do-not-expose-this',
 *       secret: 'or-this',
 *     },
 *   },
 * }
 * const redactedPayload = redactSensitiveFields(payload)
 * console.log(redactedPayload) // { password: '[REDACTED]', secret: '[REDACTED]', nested: { accessToken: '[REDACTED]' }, deeplyNested: { nested: { password: '[REDACTED]', secret: '[REDACTED]' } } }
 *
 * @param payload - The payload to redact sensitive fields from.
 * @returns The redacted payload.
 *
 * Note: This function only processes nested objects up to 5 levels deep. Anything deeper than that will be redacted as '[TOO DEEP]'.
 */
export function redactSensitiveFields(payload: Record<PropertyKey, unknown>) {
  // We need to clone the payload to avoid mutating the original payload
  const redactedPayload = safeStructuredClone(payload)

  // Then we'll use a recursive function redact the sensitive fields
  // This low-level function mutates the payload in place for better performance
  // so we aren't repeatedly cloning the payload in nested objects - we do this once at the top level
  redactSensitiveFieldsInPlace(redactedPayload)

  return redactedPayload
}

function redactSensitiveFieldsInPlace(
  payload: Record<PropertyKey, unknown>,
  currentDepth = 0,
  maxDepth = 5,
) {
  for (const [fieldName, fieldValue] of Object.entries(payload)) {
    // Handle sensitive fields first
    if (SENSITIVE_FIELD_PATTERNS.some((pattern) => pattern.test(fieldName))) {
      payload[fieldName] = Array.isArray(fieldValue)
        ? fieldValue.map(() => '[REDACTED]')
        : '[REDACTED]'
      continue
    }

    // Skip non-objects
    if (
      !fieldValue ||
      typeof fieldValue !== 'object' ||
      Array.isArray(fieldValue)
    ) {
      continue
    }

    // Handle depth limit
    if (currentDepth >= maxDepth) {
      payload[fieldName] = '[TOO DEEP]'
      continue
    }

    // Recurse into nested object
    redactSensitiveFieldsInPlace(
      // oxlint-disable-next-line typescript/consistent-type-assertions -- grandfathered: generic-narrow
      fieldValue as Record<PropertyKey, unknown>,
      currentDepth + 1,
      maxDepth,
    )
  }
}

/**
 * Clones an error and redacts sensitive fields from inside it.
 *
 * This may change the returned type of the error (since fields could be deleted or replaced), so don't try and access properties on the redacted error
 * Just use this for logging purposes.
 */
export function redactError(error: Error) {
  const redactedError = cloneError(error)
  // Iterate over all enumerable properties of the error instance
  for (const [key, value] of Object.entries(redactedError)) {
    // Check if the property name matches sensitive field patterns
    if (SENSITIVE_FIELD_PATTERNS.some((pattern) => pattern.test(key))) {
      if (Array.isArray(value)) {
        // @ts-expect-error - we know the key is valid because we're iterating over the error object
        redactedError[key] = value.map(() => '[REDACTED]')
      } else {
        // @ts-expect-error - we know the key is valid because we're iterating over the error object
        redactedError[key] = '[REDACTED]'
      }
    }

    // If the property value is an object, recurse into it to redact sensitive fields
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      redactSensitiveFieldsInPlace(
        value,
        1, // Start at depth 1 since we're already inside the error
      )
    }
  }

  return redactedError
}
